/* eslint-disable no-restricted-syntax */
import { useEffect, useReducer, useRef, useState } from "react";
import { Accordion } from "react-bootstrap";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";
import { Flip, toast } from "react-toastify";

import { createQuotation, getLatestDraft, getQuotationDraft, saveQuotationDraft } from "api";
import errorToast from "helpers/errorToast";
import {
  DraftDataType,
  getFirstSectionWithErrors,
  modifyDraftData,
  parseDraftValues,
  resolveServerErrors,
} from "helpers/quotationFormHelper";
import { QuotationFormStepTypes, QuotationFormStepValidationTypes, ShipmentTypes } from "constants/";
import { useGetQuoteRequestDetails } from "api/queries/quotes/useGetQuoteRequestDetails";
import { useGetQuotationFormConfig } from "api/queries/quotation-form/useGetQuotationFormConfig";
import { initialState, reducer, QuotationFormActionType } from "./QuotationFormState";
import ServiceForm from "./components/Service/ServiceForm";
import CostForm from "./components/Costs/CostsForm";
import ScheduleForm from "./components/Schedule/ScheduleForm";
import MiscForm from "./components/Misc/MiscForm";
import Header from "./components/Header/Header";
import FormConfirmation from "./components/FormConfirmation/FormConfirmation";

function QuotationForm() {
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [modifiedDate, setModifiedDate] = useState<string | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const currentActiveStep = searchParams.get("active_step");
  const draftId = searchParams.get("draft_id");
  const id = useParams().id?.toUpperCase();

  const methods = useForm<any>({
    mode: "onChange",
    defaultValues: {
      service_confirmation: {
        custom: [],
      },
    },
  });
  const { clearErrors, formState, getValues, reset, trigger, setError } = methods;

  const formStepRefs = {
    [QuotationFormStepTypes.SERVICE]: useRef<any>(null),
    [QuotationFormStepTypes.COSTS]: useRef<any>(null),
    [QuotationFormStepTypes.SCHEDULE]: useRef<any>(null),
    [QuotationFormStepTypes.MISC]: useRef<any>(null),
  };

  // Get quote request details
  const { data } = useGetQuoteRequestDetails({
    shipmentId: id!,
    options: {
      enabled: !!id,
      refetchOnMount: false,
    },
  });
  const isMultiStop = data?.organization.shipment_type === ShipmentTypes.MULTISTOP;

  const { data: quotationConfigData } = useGetQuotationFormConfig(id!, {
    enabled: !!id,
    refetchOnMount: false,
  });

  useEffect(() => {
    if (!currentActiveStep) {
      searchParams.set("active_step", QuotationFormStepTypes.SERVICE);
      setSearchParams(searchParams);
    }
  }, []);

  useEffect(() => {
    if (!draftId && id) {
      getLatestDraft(id).then((res: any) => {
        if (!res?.data?.reference_number) return;
        setModifiedDate(res?.data?.modified);
        searchParams.set("draft_id", res.data.reference_number);
        setSearchParams(searchParams);
      });
      return;
    }

    if (!draftId || getValues("service.freight_mode")) return;

    getQuotationDraft(draftId)
      .then((res: any) => {
        const data: DraftDataType = res?.data?.data;
        setModifiedDate(res?.data?.modified);
        reset(modifyDraftData(data));
      })
      .catch((err) => {
        if (err?.response?.status === 404) {
          searchParams.delete("draft_id");
          setSearchParams(searchParams);
        }
        errorToast(err);
      });
  }, [draftId]);

  const handleDraftSave = async () => {
    if (!currentActiveStep) return;

    clearErrors(QuotationFormStepValidationTypes[currentActiveStep]);
    if (formStepRefs[currentActiveStep]?.current?.checkStepValidation) {
      formStepRefs[currentActiveStep].current.checkStepValidation();
    }
    let isValid = true;
    if (QuotationFormStepValidationTypes[currentActiveStep]) {
      isValid = await trigger(QuotationFormStepValidationTypes[currentActiveStep]);
    }
    if (isValid && draftId && getValues("service.freight_mode")) {
      setIsSaving(true);
      saveQuotationDraft(
        draftId,
        parseDraftValues(getValues(), quotationConfigData, data?.schedule?.stops),
      )
        .then((res: any) => {
          setModifiedDate(res?.data?.modified);
          dispatch({
            type: QuotationFormActionType.UPDATE_DRAFT_SAVE,
            payload: { key: currentActiveStep, value: true },
          });
        })
        .finally(() => {
          setIsSaving(false);
          toast.success("Draft saved successfully", {
            position: toast.POSITION.TOP_CENTER,
            hideProgressBar: true,
            autoClose: 1500,
            transition: Flip,
          });
        });
    }
  };

  const handleAccordionChange = (eventKey: string | null) => {
    handleDraftSave();

    if (!eventKey) {
      searchParams.delete("active_step");
    } else {
      searchParams.set("active_step", eventKey);
    }
    setSearchParams(searchParams);
  };

  const handleSubmit = async () => {
    if (!draftId) return;

    const isValid = await trigger(undefined, { shouldFocus: true });
    if (!isValid) {
      const sectionErrorKeys = Object.keys(formState?.errors ?? {});
      if (sectionErrorKeys.length > 0) {
        const firstSectionWithError = getFirstSectionWithErrors(sectionErrorKeys);
        if (!firstSectionWithError) return;
        searchParams.set("active_step", firstSectionWithError);
        return;
      }
    }

    setIsSubmitting(true);
    saveQuotationDraft(draftId, parseDraftValues(getValues(), quotationConfigData, data?.schedule?.stops))
      .then(() => createQuotation(draftId))
      .then(() => navigate(`/shipment/${id}`))
      .catch((err) => {
        resolveServerErrors(err, setError);
        errorToast(err);
      })
      .finally(() => setIsSubmitting(false));
  };

  return (
    <FormProvider {...methods}>
      <form className="request-form" id="request-form" noValidate>
        <Header quoteRequestDetails={data} state={state} modifiedDate={modifiedDate} />
        <Accordion onSelect={handleAccordionChange} activeKey={currentActiveStep || ""}>
          <ServiceForm state={state} childRef={formStepRefs[QuotationFormStepTypes.SERVICE]} />

          {isMultiStop ? (
            <>
              <ScheduleForm
                state={state}
                quoteRequestDetails={data}
                childRef={formStepRefs[QuotationFormStepTypes.SCHEDULE]}
                isMultiStop
              />
              <CostForm
                state={state}
                quoteRequestDetails={data}
                childRef={formStepRefs[QuotationFormStepTypes.COSTS]}
                dispatch={dispatch}
              />
            </>
          ) : (
            <>
              <CostForm
                state={state}
                quoteRequestDetails={data}
                childRef={formStepRefs[QuotationFormStepTypes.COSTS]}
                dispatch={dispatch}
              />
              <ScheduleForm
                state={state}
                quoteRequestDetails={data}
                childRef={formStepRefs[QuotationFormStepTypes.SCHEDULE]}
              />
            </>
          )}

          <MiscForm state={state} childRef={formStepRefs[QuotationFormStepTypes.MISC]} />
          <FormConfirmation
            state={state}
            isSubmitting={isSubmitting}
            isSaving={isSaving}
            onSubmit={handleSubmit}
            onDraftSave={handleDraftSave}
            quoteRequestDetails={data}
          />
        </Accordion>
      </form>
    </FormProvider>
  );
}

export default QuotationForm;
